import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaFileContract, FaGavel, FaHandshake } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "September 2026";

  return (
    <div className="min-h-screen bg-gradient-dark">
      <Navbar />
      <div className="py-20 px-4 sm:px-6 lg:px-8 pt-28">

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <FaFileContract className="text-6xl text-emerald-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Terms of Service</span>
          </h1>
          <p className="text-gray-400">Last Updated: {lastUpdated}</p>
          <p className="text-gray-400 mt-2">Apex Legal Systems LLC</p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 md:p-12 space-y-8 text-gray-300 leading-relaxed"
        >
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <FaHandshake className="text-emerald-400 mr-3" />
              Agreement to Terms
            </h2>
            <p>
              These Terms of Service ("Terms") constitute a legal agreement between you ("Client," "you," or "your") and Apex Legal Systems LLC ("Apex," "we," "us," or "our") governing your use of our AI-powered legal intake services and software platform.
            </p>
            <p className="mt-4">
              <strong className="text-white">By signing an order form, paying our invoice, or using our services, you agree to be bound by these Terms.</strong> If you do not agree, do not use our services.
            </p>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Service Description</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">1.1 Services Provided</h3>
            <p>Apex provides HIPAA-compliant AI-powered intake solutions for personal injury law firms, including:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>24/7 AI voice receptionist for inbound calls</li>
              <li>Mobile-optimized intake forms</li>
              <li>Automated lead qualification and routing</li>
              <li>SMS and email auto-responders</li>
              <li>Follow-up sequences and client reminders</li>
              <li>Medical records request automation</li>
              <li>Real-time analytics and reporting dashboard</li>
              <li>Training and ongoing support</li>
            </ul>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">1.2 Service Plans</h3>
            <p>We offer the following service plans:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">The Apex System:</strong> $1,500/month and $3,000 setup per attorney, including all core features.</li>
              <li><strong className="text-white">Enterprise:</strong> For firms with 11+ attorneys, multiple locations, or custom integration needs, quoted individually.</li>
            </ul>
            <p className="mt-4">
              Specific features, pricing, and terms for your plan are outlined in your Order Form.
            </p>
          </section>

          {/* Account Terms */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Account Terms</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">2.1 Eligibility</h3>
            <p>
              Our services are intended for licensed law firms handling personal injury cases. You must be authorized to enter into this agreement on behalf of your law firm.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">2.2 Account Security</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You must notify us immediately of any unauthorized access</li>
              <li>You are responsible for all activities under your account</li>
              <li>You must not share login credentials with unauthorized persons</li>
            </ul>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">2.3 Acceptable Use</h3>
            <p>You agree NOT to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Use our services for any unlawful purpose</li>
              <li>Attempt to access our systems without authorization</li>
              <li>Reverse engineer, decompile, or disassemble our software</li>
              <li>Use our services to transmit malware or harmful code</li>
              <li>Resell or redistribute our services without written permission</li>
              <li>Use our services to violate HIPAA or other privacy laws</li>
            </ul>
          </section>

          {/* Payment Terms */}
          <section className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-4">3. Payment Terms</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">3.1 Fees and Billing</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">Setup Fee:</strong> 100% due upfront before work begins (one-time, non-refundable)</li>
              <li><strong className="text-white">Monthly Retainer:</strong> Billing begins on your go-live date.</li>
              <li><strong className="text-white">Billing Cycle:</strong> Monthly, charged on the same day each month</li>
              <li><strong className="text-white">Payment Method:</strong> Credit card, ACH, or wire transfer via Stripe</li>
              <li><strong className="text-white">Late Payments:</strong> Services may be suspended for accounts 7+ days overdue</li>
            </ul>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">3.2 Price Changes</h3>
            <p>
              We may change our pricing with 30 days' written notice. Price changes will not affect your current billing cycle. You may cancel if you do not agree to the price increase.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">3.3 Taxes</h3>
            <p>
              All fees are exclusive of applicable taxes. You are responsible for all sales, use, and excise taxes, and any other similar taxes, duties, and charges.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">3.4 Refunds</h3>
            <p>
              Setup fees are non-refundable. Monthly retainers are non-refundable but you may cancel future billing per Section 4.2.
            </p>
          </section>

          {/* Term and Termination */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Term and Termination</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">4.1 Service Term</h3>
            <p>
              After your initial setup period, services continue on a month-to-month basis. There are no long-term contracts.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">4.2 Cancellation by Client</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>You may cancel at any time with 30 days' written notice</li>
              <li>Cancellation must be sent to: <a href="mailto:contact@apexlegalsystems.com" className="text-emerald-400 hover:text-emerald-300">contact@apexlegalsystems.com</a></li>
              <li>Services will continue until the end of your paid billing period</li>
              <li>No refunds will be provided for the current billing cycle</li>
              <li>You will receive an export of your data upon request</li>
            </ul>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">4.3 Termination by Apex</h3>
            <p>We may suspend or terminate your account if:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>You breach these Terms</li>
              <li>Your account is 15+ days past due</li>
              <li>You use our services unlawfully or unethically</li>
              <li>Continued service poses a security or legal risk</li>
            </ul>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">4.4 Effect of Termination</h3>
            <p>Upon termination:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Your access to our services will be immediately disabled</li>
              <li>We will provide a data export within 30 days if requested</li>
              <li>We will retain your data per our Privacy Policy and HIPAA requirements</li>
              <li>Outstanding fees remain due and payable</li>
            </ul>
          </section>

          {/* HIPAA Compliance */}
          <section className="bg-slate-700/50 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-4">5. HIPAA Compliance</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">5.1 Business Associate Agreement</h3>
            <p>
              <strong className="text-white">A signed Business Associate Agreement (BAA) is required before we process any Protected Health Information (PHI).</strong> The BAA is incorporated into these Terms by reference.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">5.2 Your Responsibilities</h3>
            <p>As a Covered Entity, you agree to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Use our services in compliance with HIPAA</li>
              <li>Obtain necessary patient authorizations for PHI use</li>
              <li>Train your staff on HIPAA requirements</li>
              <li>Notify us of any potential PHI breaches involving our services</li>
              <li>Maintain your own HIPAA compliance program</li>
            </ul>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">5.3 Our Responsibilities</h3>
            <p>We agree to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Use and disclose PHI only as permitted by the BAA</li>
              <li>Implement appropriate safeguards for PHI</li>
              <li>Report any security incidents or breaches</li>
              <li>Ensure our subcontractors sign BAAs</li>
              <li>Make PHI available for amendment and accounting of disclosures</li>
            </ul>
          </section>

          {/* Warranties and Disclaimers */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Warranties and Disclaimers</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">6.1 Our Warranties</h3>
            <p>We warrant that our services will:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Perform substantially as described in our documentation</li>
              <li>Be provided in a professional manner</li>
              <li>Comply with applicable HIPAA requirements</li>
            </ul>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">6.2 Disclaimer</h3>
            <p className="uppercase font-bold text-white mt-4">
              EXCEPT AS EXPRESSLY PROVIDED, OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
            <p className="mt-4">
              We do not guarantee that our services will be uninterrupted, error-free, or completely secure. AI-based features may produce imperfect results.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-4">7. Limitation of Liability</h2>
            
            <p className="uppercase font-bold text-white">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>
                <strong className="text-white">INDIRECT DAMAGES:</strong> WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, LOST REVENUE, OR LOST DATA.
              </li>
              <li>
                <strong className="text-white">TOTAL LIABILITY CAP:</strong> OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING FROM THESE TERMS WILL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
              </li>
              <li>
                <strong className="text-white">EXCEPTIONS:</strong> Nothing in these Terms limits our liability for fraud, gross negligence, willful misconduct, or violations of HIPAA.
              </li>
            </ul>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Apex, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorneys' fees) arising from:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Your use of our services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any law or regulation</li>
              <li>Your violation of third-party rights</li>
              <li>Any PHI breach caused by your negligence</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">9.1 Our IP</h3>
            <p>
              We retain all rights, title, and interest in our services, software, AI models, documentation, and branding. These Terms do not grant you any ownership rights.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">9.2 Your Data</h3>
            <p>
              You retain all rights to your data. You grant us a limited license to use your data solely to provide our services. We may use de-identified, aggregated data to improve our services.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">9.3 Feedback</h3>
            <p>
              If you provide feedback or suggestions, we may use them without obligation to you.
            </p>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Modifications to Terms</h2>
            <p>
              We may modify these Terms at any time. Material changes will be communicated via:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Email to your account administrator</li>
              <li>Notice on our website</li>
              <li>In-app notification</li>
            </ul>
            <p className="mt-4">
              Continued use of our services after changes constitutes acceptance. If you do not agree, you may cancel per Section 4.2.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <FaGavel className="text-emerald-400 mr-3" />
              11. Dispute Resolution
            </h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">11.1 Informal Resolution</h3>
            <p>
              Before filing a lawsuit, you agree to contact us at <a href="mailto:contact@apexlegalsystems.com" className="text-emerald-400 hover:text-emerald-300">contact@apexlegalsystems.com</a> to attempt informal resolution.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">11.2 Arbitration</h3>
            <p>
              If informal resolution fails, disputes will be resolved through binding arbitration under the American Arbitration Association's Commercial Arbitration Rules. The arbitration will be conducted virtually or in the location where your law firm is based.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">11.3 Class Action Waiver</h3>
            <p>
              You agree to resolve disputes individually and waive any right to participate in class actions or class arbitrations.
            </p>
          </section>

          {/* General Provisions */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. General Provisions</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">12.1 Governing Law</h3>
            <p>
              These Terms are governed by the laws of the State of Wyoming, without regard to conflict of law principles.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">12.2 Entire Agreement</h3>
            <p>
              These Terms, together with your Order Form and BAA, constitute the entire agreement between you and Apex.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">12.3 Severability</h3>
            <p>
              If any provision of these Terms is found unenforceable, the remaining provisions will remain in effect.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">12.4 No Waiver</h3>
            <p>
              Our failure to enforce any provision does not waive our right to enforce it later.
            </p>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">12.5 Assignment</h3>
            <p>
              You may not assign these Terms without our written consent. We may assign these Terms in connection with a merger, acquisition, or sale of assets.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-slate-700/50 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-4">13. Contact Information</h2>
            <p className="mb-4">
              Questions about these Terms? Contact us:
            </p>
            <div className="space-y-2">
              <p><strong className="text-white">Apex Legal Systems LLC</strong></p>
              <p>[Your Wyoming Registered Agent Address]</p>
              <p>Email: <a href="mailto:contact@apexlegalsystems.com" className="text-emerald-400 hover:text-emerald-300">contact@apexlegalsystems.com</a></p>
              <p>Website: <a href="https://ApexLegalSystems.com" className="text-emerald-400 hover:text-emerald-300">ApexLegalSystems.com</a></p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-6">
            <p className="font-bold text-white mb-4">
              BY USING OUR SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
            </p>
            <p className="text-sm text-gray-400">
              If you have questions or need clarification on any provision, please contact us before using our services.
            </p>
          </section>
        </motion.div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
